import { createTheme, Theme, PaletteOptions } from '@mui/material/styles';

// Extend MUI palette to include accent color
declare module '@mui/material/styles' {
  interface Palette {
    accent: Palette['primary'];
  }
  interface PaletteOptions {
    accent?: PaletteOptions['primary'];
  }
}

// Extend Button props to support accent color
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    accent: true;
  }
}

// Extend Fab props to support accent color
declare module '@mui/material/Fab' {
  interface FabPropsColorOverrides {
    accent: true;
  }
}

// Extend Chip props to support accent color
declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    accent: true;
  }
}

/**
 * Calculate relative luminance of a color (WCAG formula)
 * Returns a value between 0 (darkest) and 1 (lightest)
 */
function getLuminance(hexColor: string): number {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Apply gamma correction
  const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  
  // Calculate luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Determines if a background color is dark enough to require light text
 * Returns true if the background is dark (luminance < 0.5)
 */
export function isBackgroundDark(backgroundColor: string): boolean {
  try {
    const luminance = getLuminance(backgroundColor);
    return luminance < 0.5;
  } catch (error) {
    console.warn('Error calculating luminance for color:', backgroundColor);
    return false; // Default to light background
  }
}

/**
 * Get appropriate text colors based on background darkness
 */
function getTextColorsForBackground(backgroundColor: string): { primary: string; secondary: string } {
  if (isBackgroundDark(backgroundColor)) {
    return {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
    };
  }
  return {
    primary: DEFAULT_THEME_VALUES.palette.text.primary,
    secondary: DEFAULT_THEME_VALUES.palette.text.secondary,
  };
}

// Function to load Google Fonts dynamically
export function loadGoogleFonts(fonts: string[]) {
  console.log('Loading Google Fonts:', fonts);
  
  const existingLinks = document.querySelectorAll('link[data-font-loader]');
  existingLinks.forEach(link => link.remove());

  if (fonts.length === 0) return;

  const fontParams = fonts
    .filter(font => font && font.trim())
    .map(font => `family=${font.replace(/\s+/g, '+')}`)
    .join('&');

  if (fontParams) {
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?${fontParams}&display=swap`;
    link.rel = 'stylesheet';
    link.setAttribute('data-font-loader', 'true');
    document.head.appendChild(link);
    console.log('Font link added:', link.href);
  }
}

// Current hardcoded theme values as defaults (from index.tsx)
const DEFAULT_THEME_VALUES = {
  palette: {
    mode: 'light' as const,
    background: {
      default: '#F7F9FB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#111827',
      secondary: '#6B7280',
    },
    primary: {
      main: '#07C15B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0099FF',
    },
    accent: {
      main: '#FFBD59',
      contrastText: '#FFFFFF',
    },
    divider: 'rgba(17,24,39,0.08)',
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: 'Poppins, sans-serif',
    headingFontFamily: '"Bebas Neue", sans-serif',
    headingLetterSpacing: 0.5,
    buttonFontWeight: 600,
    buttonTextTransform: 'none' as const,
  },
};

// AI-generated theme properties interface
export interface AIThemeProperties {
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    surface?: string;
    textPrimary?: string;
    textSecondary?: string;
  };
  typography?: {
    headingFont?: string;
    bodyFont?: string;
    headingWeight?: number;
    headingLetterSpacing?: number;
  };
  spacing?: {
    borderRadius?: number;
    componentSpacing?: 'compact' | 'comfortable' | 'spacious';
  };
}

// Business theme data from Firebase
export interface BusinessThemeData {
  businessId: string;
  aiGenerated?: boolean;
  themeProperties?: AIThemeProperties;
  generatedAt?: string;
  generationPrompt?: string;
}

/**
 * Builds a MUI theme with AI-generated properties and comprehensive fallbacks
 * If no AI properties are provided, returns the exact current default theme
 */
export function buildThemeFromAI(aiProperties?: AIThemeProperties): Theme {
  // If no AI properties, return current default theme exactly
  if (!aiProperties) {
    return createDefaultTheme();
  }

  return createTheme({
    palette: {
      mode: 'light',
      background: {
        default: aiProperties.colors?.background || DEFAULT_THEME_VALUES.palette.background.default,
        paper: aiProperties.colors?.surface || DEFAULT_THEME_VALUES.palette.background.paper,
      },
      text: {
        primary: aiProperties.colors?.textPrimary || DEFAULT_THEME_VALUES.palette.text.primary,
        secondary: aiProperties.colors?.textSecondary || DEFAULT_THEME_VALUES.palette.text.secondary,
      },
      primary: {
        main: aiProperties.colors?.primary || DEFAULT_THEME_VALUES.palette.primary.main,
        contrastText: DEFAULT_THEME_VALUES.palette.primary.contrastText,
      },
      secondary: {
        main: aiProperties.colors?.secondary || DEFAULT_THEME_VALUES.palette.secondary.main,
      },
      accent: {
        main: aiProperties.colors?.accent || DEFAULT_THEME_VALUES.palette.accent.main,
        contrastText: DEFAULT_THEME_VALUES.palette.accent.contrastText,
      },
      divider: DEFAULT_THEME_VALUES.palette.divider,
    },

    shape: { 
      borderRadius: aiProperties.spacing?.borderRadius || DEFAULT_THEME_VALUES.shape.borderRadius 
    },

    typography: {
      fontFamily: aiProperties.typography?.bodyFont || DEFAULT_THEME_VALUES.typography.fontFamily,
      h1: { 
        fontFamily: aiProperties.typography?.headingFont || DEFAULT_THEME_VALUES.typography.headingFontFamily,
        letterSpacing: aiProperties.typography?.headingLetterSpacing ?? DEFAULT_THEME_VALUES.typography.headingLetterSpacing,
        fontWeight: aiProperties.typography?.headingWeight || 400,
      },
      h2: { 
        fontFamily: aiProperties.typography?.headingFont || DEFAULT_THEME_VALUES.typography.headingFontFamily,
        letterSpacing: aiProperties.typography?.headingLetterSpacing ?? DEFAULT_THEME_VALUES.typography.headingLetterSpacing,
      },
      h3: { 
        fontFamily: aiProperties.typography?.headingFont || DEFAULT_THEME_VALUES.typography.headingFontFamily,
      },
      h4: { 
        fontFamily: aiProperties.typography?.headingFont || DEFAULT_THEME_VALUES.typography.headingFontFamily,
      },
      h5: { 
        fontFamily: aiProperties.typography?.headingFont || DEFAULT_THEME_VALUES.typography.headingFontFamily,
      },
      h6: { 
        fontFamily: aiProperties.typography?.headingFont || DEFAULT_THEME_VALUES.typography.headingFontFamily,
      },
      button: { 
        fontWeight: DEFAULT_THEME_VALUES.typography.buttonFontWeight,
        textTransform: DEFAULT_THEME_VALUES.typography.buttonTextTransform,
      },
    },

    // Keep all existing component overrides exactly as they are
    components: getDefaultComponentOverrides(aiProperties),
  });
}

/**
 * Creates the exact current default theme (for backward compatibility)
 */
function createDefaultTheme(): Theme {
  return createTheme({
    palette: {
      mode: 'light',
      background: {
        default: '#F7F9FB',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#111827',
        secondary: '#6B7280',
      },
      primary: {
        main: '#07C15B',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#0099FF',
      },
      accent: {
        main: '#FFBD59',
        contrastText: '#FFFFFF',
      },
      divider: 'rgba(17,24,39,0.08)',
    },

    shape: { borderRadius: 12 },

    typography: {
      fontFamily: 'Poppins, sans-serif',
      h1: { fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 0.5 },
      h2: { fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 0.5 },
      h3: { fontFamily: '"Bebas Neue", sans-serif' },
      h4: { fontFamily: '"Bebas Neue", sans-serif' },
      h5: { fontFamily: '"Bebas Neue", sans-serif' },
      h6: { fontFamily: '"Bebas Neue", sans-serif' },
      button: { fontWeight: 600, textTransform: 'none' },
    },

    components: getDefaultComponentOverrides(),
  });
}

/**
 * Returns component overrides with optional AI customizations
 */
function getDefaultComponentOverrides(aiProperties?: AIThemeProperties) {
  const borderRadius = aiProperties?.spacing?.borderRadius || 12;
  const primaryColor = aiProperties?.colors?.primary || '#07C15B';
  const secondaryColor = aiProperties?.colors?.secondary || '#0099FF';
  const backgroundColor = aiProperties?.colors?.background || '#F7F9FB';
  const surfaceColor = aiProperties?.colors?.surface || '#FFFFFF';
  const textPrimary = aiProperties?.colors?.textPrimary || '#111827';
  const textSecondary = aiProperties?.colors?.textSecondary || '#6B7280';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        '*, *::before, *::after': { boxSizing: 'border-box' },
        body: {
          backgroundColor: backgroundColor,
          color: textPrimary,
        },
        '::selection': {
          backgroundColor: `rgba(7,193,91,0.22)`, // Keep original selection color
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(17,24,39,0.06)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: surfaceColor,
          color: textPrimary,
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: surfaceColor,
          color: textPrimary,
          borderRadius: borderRadius,
        },
        input: {
          color: textPrimary,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: 'rgba(17,24,39,0.12)' },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(17,24,39,0.24)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
            boxShadow: `0 0 0 3px rgba(7,193,91,0.18)`,
          },
        },
        input: { paddingTop: 12, paddingBottom: 12 },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: textSecondary,
          '&.Mui-focused': { color: primaryColor },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#9CA3AF',
          '&.Mui-checked': { color: primaryColor },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          borderRadius: borderRadius,
        },
        containedPrimary: {
          boxShadow: `0 6px 16px rgba(0,0,0,0.08)`,
        },
        containedSecondary: {
          boxShadow: `0 6px 16px rgba(0,0,0,0.08)`,
        },
        containedAccent: {
          backgroundColor: '#FFBD59',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#FFB040',
          },
          boxShadow: `0 6px 16px rgba(0,0,0,0.08)`,
        },
      },
    },

    MuiChip: {
      styleOverrides: { 
        root: { borderRadius: Math.max(10, borderRadius - 2) },
      },
    },
  };
}

/**
 * Validates AI theme properties and provides safe fallbacks
 */
export function validateAIThemeProperties(properties: any): AIThemeProperties | null {
  if (!properties || typeof properties !== 'object') {
    return null;
  }

  const validated: AIThemeProperties = {};

  // Validate colors
  if (properties.colors && typeof properties.colors === 'object') {
    validated.colors = {};
    const colorFields = ['primary', 'secondary', 'accent', 'background', 'surface', 'textPrimary', 'textSecondary'];
    
    colorFields.forEach(field => {
      const color = properties.colors[field];
      if (typeof color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(color)) {
        validated.colors![field as keyof typeof validated.colors] = color;
      }
    });
  }

  // Validate typography
  if (properties.typography && typeof properties.typography === 'object') {
    validated.typography = {};
    
    if (typeof properties.typography.headingFont === 'string') {
      validated.typography.headingFont = properties.typography.headingFont;
    }
    if (typeof properties.typography.bodyFont === 'string') {
      validated.typography.bodyFont = properties.typography.bodyFont;
    }
    if (typeof properties.typography.headingWeight === 'number' && 
        properties.typography.headingWeight >= 100 && 
        properties.typography.headingWeight <= 900) {
      validated.typography.headingWeight = properties.typography.headingWeight;
    }
    if (typeof properties.typography.headingLetterSpacing === 'number') {
      validated.typography.headingLetterSpacing = properties.typography.headingLetterSpacing;
    }
  }

  // Validate spacing
  if (properties.spacing && typeof properties.spacing === 'object') {
    validated.spacing = {};
    
    if (typeof properties.spacing.borderRadius === 'number' && 
        properties.spacing.borderRadius >= 0 && 
        properties.spacing.borderRadius <= 50) {
      validated.spacing.borderRadius = properties.spacing.borderRadius;
    }
    if (['compact', 'comfortable', 'spacious'].includes(properties.spacing.componentSpacing)) {
      validated.spacing.componentSpacing = properties.spacing.componentSpacing;
    }
  }

  // Return null if no valid properties found
  if (Object.keys(validated).length === 0) {
    return null;
  }

  return validated;
}
